/**
 * SAILINGS LAB — chapter 9, where the two tracks are finally the same picture as the arithmetic.
 *
 * FOUNDER's overlay list: "Sailings: great circle vs rhumb line, departure, difference of longitude,
 * convergence of meridians."
 *
 * NOTE ON THE CHAPTER NUMBER. The list said chapter 5. The ladder says chapter 5 is "Flotation,
 * Displacement and Density" and THE SAILINGS is chapter 9. Built against the ladder, because putting
 * a navigation instrument inside a stability chapter would have made the ladder wrong rather than
 * the lab.
 *
 * ── WHAT IT TEACHES, AND WHY IT IS THE SAME GLOBE ─────────────────────────────────────────────
 * A candidate meets the sailings as formulae — dLat, dLong, departure, mean latitude — and can pass
 * a paper without ever seeing that departure IS the shrinking he watched in chapter 1. This lab
 * closes that: the two tracks are drawn on the same Earth he has been dragging since the beginning,
 * and every quantity beside it is computed from the two positions rather than looked up.
 *
 * ── EVERY NUMBER HERE IS COMPUTED, AND ONE IS DELIBERATELY REFUSED ────────────────────────────
 * dLat, dLong, departure and the great-circle distance come out of the geometry exactly. MEAN
 * LATITUDE sailing is an APPROXIMATION — it is what a mariner uses and it is not the true distance —
 * so it is labelled as one and shown beside the exact figure rather than instead of it. A candidate
 * who does not know which of his answers is exact will trust the wrong one on a long leg.
 */

import { useState } from 'react';
import NavSphere, { dm, bearing } from './navsphere.jsx';
import { Value } from '../../../packages/ankr-glass/src/react.jsx';
import { computed, refused, cited } from '../../../packages/ankr-provenance/src/provenance.mjs';

const RAD = Math.PI / 180;

const LEGS = [
  { id: 'atl', label: 'Fastnet → New York', from: [51.5, -9.5], to: [40.5, -74.0] },
  { id: 'cape', label: 'Cape Town → Fremantle', from: [-34.0, 18.5], to: [-32.0, 115.7] },
  { id: 'trade', label: 'Equatorial crossing', from: [2.0, -25.0], to: [2.0, 25.0] },
];

/** Great-circle distance in nautical miles — a minute of arc IS a nautical mile, which is the point. */
function gcDist(a, b) {
  const [p1, l1] = [a[0] * RAD, a[1] * RAD], [p2, l2] = [b[0] * RAD, b[1] * RAD];
  const d = 2 * Math.asin(Math.sqrt(Math.sin((p2 - p1) / 2) ** 2
          + Math.cos(p1) * Math.cos(p2) * Math.sin((l2 - l1) / 2) ** 2));
  return (d / RAD) * 60;
}

export default function SailingsLab() {
  const [i, setI] = useState(0);
  const [show, setShow] = useState({ graticule: true, equator: true, convergence: false, primeMeridian: false });
  const leg = LEGS[i];
  const [a, b] = [leg.from, leg.to];

  const dLat = (b[0] - a[0]) * 60;                                   // minutes
  const dLong = (((b[1] - a[1] + 540) % 360) - 180) * 60;            // minutes, wrapped
  const mLat = (a[0] + b[0]) / 2;
  const dep = dLong * Math.cos(mLat * RAD);                          // mean-latitude departure
  const gc = gcDist(a, b);
  const rhumbDist = Math.hypot(dLat, dep);                           // plane sailing on the mean lat
  const saved = rhumbDist - gc;

  return (
    <div className="g-glass shell-panel lab">
      <div className="g-label">Sailings lab · two ways across</div>
      <h2 className="g-h2 lab-h">The shorter way is not the straight one</h2>
      <p className="g-body">
        The <b className="lab-key lab-gc">great circle</b> is the shortest track between two places
        on the Earth. The <b className="lab-key lab-rl">rhumb line</b> holds one course and is longer.
        Turn the globe and look along each.
      </p>

      <div className="lab-globe">
        <NavSphere height={320} overlays={show} route={{ from: a, to: b }}
          marks={[{ lat: a[0], lon: a[1], label: 'from', arcs: false, colour: '#4fd08a' },
                  { lat: b[0], lon: b[1], label: 'to', arcs: false, colour: '#e0642a' }]} />
      </div>

      <div className="shell-picker lab-toggles">
        {LEGS.map((l, k) => (
          <button key={l.id} onClick={() => setI(k)}
            className={`g-btn ${i === k ? 'g-btn-primary' : ''}`}>{l.label}</button>
        ))}
      </div>
      <div className="shell-picker lab-toggles">
        <button onClick={() => setShow((s) => ({ ...s, convergence: !s.convergence }))}
          className={`g-btn ${show.convergence ? 'g-btn-primary' : ''}`}>Show the convergence</button>
      </div>

      {/* The quantities, in the order a candidate works them, each computed from the two positions. */}
      <div className="lab-readout lab-sail">
        <div><span className="g-label">From</span>
             <span className="lab-num">{dm(a[0], 'lat')} {dm(a[1], 'lon')}</span></div>
        <div><span className="g-label">To</span>
             <span className="lab-num">{dm(b[0], 'lat')} {dm(b[1], 'lon')}</span></div>
        <div><span className="g-label">d.Lat</span>
             <span className="lab-num">{Math.abs(dLat).toFixed(1)}′ {dLat >= 0 ? 'N' : 'S'}</span></div>
        <div><span className="g-label">d.Long</span>
             <span className="lab-num">{Math.abs(dLong).toFixed(1)}′ {dLong >= 0 ? 'E' : 'W'}</span></div>
        <div><span className="g-label">Mean lat</span>
             <span className="lab-num">{dm(mLat, 'lat')}</span></div>
        <div><span className="g-label">Departure</span>
             <span className="lab-num lab-dep">{Math.abs(dep).toFixed(1)} nm</span></div>
      </div>

      <div className="lab-task">
        <div className="g-label">The two distances</div>
        <div className="lab-result">
          <Value of={computed(
            `Great circle ${gc.toFixed(0)} nm — exact, straight off the sphere. Rhumb line by mean ` +
            `latitude ${rhumbDist.toFixed(0)} nm. The great circle saves ${saved.toFixed(0)} nm, which ` +
            `is ${((saved / rhumbDist) * 100).toFixed(1)}% of the passage.`)} />
        </div>

        {/* The approximation named as one. */}
        <div className="lab-result">
          <Value of={refused(
            'The rhumb-line figure is MEAN LATITUDE sailing, which is an approximation: it works the ' +
            'departure at one middle latitude instead of integrating the closing all the way along. ' +
            'It is what a mariner uses and it is close enough over short legs — but on a long ' +
            'high-latitude passage it is not the true distance, and Mercator sailing is the method ' +
            'that removes the error. The great-circle figure above IS exact.')} />
        </div>

        <p className="g-body lab-why">
          Departure is <b>d.Long × cos(mean lat)</b> — the same cosine, and the same closing of the
          meridians, you switched on in chapter 1. Turn it on here and the two are one fact:
          {' '}{Math.abs(dLong).toFixed(0)}′ of longitude becomes only {Math.abs(dep).toFixed(0)} nm
          of distance at this latitude.
        </p>
      </div>

      <div className="lab-def">
        <Value of={cited(
          'The departure is the distance made good east or west, expressed in nautical miles; the '
          + 'difference of longitude is the arc of the equator intercepted between the meridians of '
          + 'the two places, and the two are equal only at the equator.',
          'Bowditch, The American Practical Navigator (1888) — public domain')} />
        <p className="g-body lab-why">
          &ldquo;Equal only at the equator&rdquo; is the whole of the sailings in six words. Try the
          equatorial leg above: there, and only there, d.Long and departure are the same number.
        </p>
      </div>
    </div>
  );
}
